'use client';

import { c as _c } from "react/compiler-runtime";
import type { Spacing } from '@onefootprint/design-tokens';
import { primitives } from '@onefootprint/design-tokens';
import * as ScrollAreaRadix from '@radix-ui/react-scroll-area';
import { cx } from 'class-variance-authority';
import type * as CSS from 'csstype';
import { forwardRef, useEffect, useRef, useState } from 'react';
type ScrollAreaProps = {
  asChild?: boolean;
  children: React.ReactNode;
  className?: string;
  hideBottomLine?: boolean;
  hideTopLine?: boolean;
  padding?: Spacing;
  paddingTop?: Spacing;
  paddingRight?: Spacing;
  paddingBottom?: Spacing;
  paddingLeft?: Spacing;
  height?: CSS.Property.Height;
  maxHeight?: CSS.Property.MaxHeight;
  overflow?: CSS.Property.Overflow;
};
const SPACING = primitives.spacing;
const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>((t0, ref) => {
  const $ = _c(23);
  const {
    children,
    className,
    asChild,
    hideBottomLine,
    hideTopLine,
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    height,
    maxHeight: t1
  } = t0;
  const maxHeight = t1 === undefined ? "100%" : t1;
  const viewportRef = useRef(null);
  const [isOverflowTop, setIsOverflowTop] = useState(false);
  const [isOverflowBottom, setIsOverflowBottom] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const scrollContainer = viewportRef.current;
      const checkOverflow = () => {
        if (scrollContainer) {
          const {
            scrollTop,
            scrollHeight,
            clientHeight
          } = scrollContainer;
          setIsOverflowTop(scrollTop > 0);
          setIsOverflowBottom(scrollTop + clientHeight < scrollHeight);
        }
      };
      checkOverflow();
      scrollContainer?.addEventListener("scroll", checkOverflow);
      return () => {
        scrollContainer?.removeEventListener("scroll", checkOverflow);
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== className) {
    t4 = cx("fp-scroll-area flex flex-col box-border min-h-full relative", className);
    $[2] = className;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = padding && SPACING[padding];
  const t6 = paddingTop && SPACING[paddingTop];
  const t7 = paddingRight && SPACING[paddingRight];
  const t8 = paddingBottom && SPACING[paddingBottom];
  const t9 = paddingLeft && SPACING[paddingLeft];
  let t10;
  if ($[4] !== height || $[5] !== maxHeight || $[6] !== t5 || $[7] !== t6 || $[8] !== t7 || $[9] !== t8 || $[10] !== t9) {
    t10 = {
      maxHeight,
      height,
      "--fp-scroll-p": t5,
      "--fp-scroll-pt": t6,
      "--fp-scroll-pr": t7,
      "--fp-scroll-pb": t8,
      "--fp-scroll-pl": t9
    };
    $[4] = height;
    $[5] = maxHeight;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = t10 as React.CSSProperties;
  const t12 = isOverflowTop && !hideTopLine;
  const t13 = isOverflowBottom && !hideBottomLine;
  let t14;
  if ($[12] !== asChild || $[13] !== children || $[14] !== t12 || $[15] !== t13) {
    t14 = <ScrollAreaRadix.Viewport ref={viewportRef} className="fp-scroll-viewport w-full box-border max-h-full min-h-full" data-overflow-top={t12} data-overflow-bottom={t13} asChild={asChild}>{children}</ScrollAreaRadix.Viewport>;
    $[12] = asChild;
    $[13] = children;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ScrollAreaRadix.Scrollbar orientation="vertical"><ScrollAreaRadix.Thumb /></ScrollAreaRadix.Scrollbar>;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== ref || $[19] !== t11 || $[20] !== t14 || $[21] !== t4) {
    t16 = <ScrollAreaRadix.Root className={t4} style={t11} ref={ref}>{t14}{t15}</ScrollAreaRadix.Root>;
    $[18] = ref;
    $[19] = t11;
    $[20] = t14;
    $[21] = t4;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  return t16;
});
export default ScrollArea;